import { useEffect, useRef, type ReactNode } from "react";
import { Maximize2, RefreshCw, Sparkles, Ellipsis } from "lucide-react";
import { cn } from "../lib/utils.js";
import { SELECTED } from "../lib/surfaces.js";
import { Tooltip } from "../molecules/Tooltip/Tooltip.js";

export type ModuleCardAccent =
  | "cyan"
  | "amber"
  | "emerald"
  | "purple"
  | "rose"
  | "neutral";

export interface ModuleCardProps {
  /** Module title shown top-left of the header. */
  readonly title: string;
  /** Small caption under the title. */
  readonly subtitle?: string;
  /** Color accent dot in the header (matches phase tint). */
  readonly accent?: ModuleCardAccent;
  /** Icon shown next to the accent dot. */
  readonly icon?: ReactNode;
  /** Optional leading header control, replacing the decorative accent dot. */
  readonly headerLeadingSlot?: ReactNode;
  /** Body content (the actual visualization). */
  readonly children: ReactNode;
  /** Optional small footer text (status, last updated, etc.). */
  readonly footer?: ReactNode;
  /** When true, draws the selected outline + lifts the card slightly. */
  readonly selected?: boolean | undefined;
  /** Visual-only disabled/locked treatment; editability remains caller-owned. */
  readonly dimmed?: boolean | undefined;
  /** Called when the user clicks the card surface (selects the module). */
  readonly onSelect?: (() => void) | undefined;
  /** Called when the user clicks the Sparkles "Ask PlayerAI" icon. */
  readonly onAskAI?: (() => void) | undefined;
  /** Optional handlers for the other header actions. */
  readonly onExpand?: (() => void) | undefined;
  readonly onRefresh?: (() => void) | undefined;
  readonly onMore?: (() => void) | undefined;
  /**
   * Optional content rendered in the header to the left of the standard
   * action icons (Sparkles / Refresh / Expand / More). Used for inline
   * controls like "+ Add stakeholder" on the Stakeholder Map card.
   * Click events stopPropagation so they don't trigger onSelect.
   */
  readonly headerExtras?: ReactNode;
  /** Extra classes for the wrapper. */
  readonly className?: string;
}

const ACCENT_DOT: Record<ModuleCardAccent, string> = {
  cyan: "bg-pb-cyan",
  amber: "bg-pb-amber",
  emerald: "bg-pb-teal",
  purple: "bg-pb-purple",
  rose: "bg-pb-red",
  neutral: "bg-pb-text-muted",
};

const ACCENT_TINT: Record<ModuleCardAccent, string> = {
  cyan: "text-pb-cyan-text",
  amber: "text-pb-amber-text",
  emerald: "text-pb-teal-text",
  purple: "text-pb-purple-text",
  rose: "text-pb-red-text",
  neutral: "text-pb-text-muted",
};

/**
 * Shared module-card primitive used across all SOLVE workspace phases.
 *
 * Layout (ported from ui-elements/components/solves/workspace/ModuleCard.tsx):
 *   • Header — `px-4 py-3 border-b border-pb-border`, accent dot + icon +
 *     title + subtitle on the left, header-action icons on the right.
 *   • Body  — `px-4 py-4 flex-1 min-h-0` slot for visualizations.
 *   • Footer (optional) — `px-4 py-2 bg-pb-card-elevated border-t`, two
 *     children rendered with `justify-between`.
 *
 * The whole card is `role="button"` when `onSelect` is supplied. Header
 * action buttons stopPropagation so they don't trigger select.
 */
export function ModuleCard({
  title,
  subtitle,
  accent = "cyan",
  icon,
  headerLeadingSlot,
  children,
  footer,
  selected,
  dimmed,
  onSelect,
  onAskAI,
  onExpand,
  onRefresh,
  onMore,
  headerExtras,
  className,
}: ModuleCardProps): JSX.Element {
  const cardRef = useRef<HTMLDivElement>(null);

  // Scroll selected module into view when triggered programmatically.
  useEffect(() => {
    if (!selected) return;
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.top < 80 || r.bottom > window.innerHeight - 40) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selected]);

  return (
    <>
      {/* Kept hand-built: the 10px-corner widget frame with its own header
          and footer bands; Card is a 14px corner on a lighter border, and no
          approved look row lists the change. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- role/tabIndex/onKeyDown are all gated on the same `onSelect` as onClick: it is a button exactly when interactive, which the rule can't prove through the ternary. */}
      <div
        ref={cardRef}
        role={onSelect ? "button" : undefined}
        tabIndex={onSelect ? 0 : undefined}
        onClick={onSelect}
        onKeyDown={(e) => {
          if (!onSelect) return;
          // Only the card itself activates on Space/Enter. Without this guard the
          // handler also fires for keys bubbling up from interactive descendants
          // (a widget body's textarea/input), so `preventDefault()` would swallow
          // every space typed into a widget — the card is "button" only when it
          // is the focused target, not its children.
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect();
          }
        }}
        aria-pressed={onSelect ? !!selected : undefined}
        className={cn(
          "pb-widget group h-full flex flex-col rounded-lg border bg-pb-card text-pb-text shadow-sm pb-hairline overflow-hidden transition-[box-shadow,border-color,opacity,transform] duration-motion-state ease-motion-standard motion-reduce:transition-none",
          onSelect ? "cursor-pointer focus-ring press" : "",
          dimmed ? "opacity-75 saturate-75" : "",
          selected
            ? SELECTED
            : "border-pb-border hover:border-pb-border-strong",
          className,
        )}
      >
        <div className="px-4 py-3 border-b border-pb-border flex items-start justify-between gap-3 shrink-0">
          <div className="flex items-start gap-2.5 min-w-0">
            {headerLeadingSlot ?? (
              // Kept hand-built: the decorative phase dot's six colours
              // (teal and purple among them) have no tint that paints them.
              <span
                aria-hidden
                className={cn(
                  "mt-1 inline-flex items-center justify-center w-2.5 h-2.5 rounded-full shrink-0",
                  ACCENT_DOT[accent],
                )}
              />
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-pb-text font-semibold text-sm tracking-tight truncate">
                {icon ? (
                  <span className={ACCENT_TINT[accent]}>{icon}</span>
                ) : null}
                <span className="truncate">{title}</span>
              </div>
              {subtitle ? (
                <div className="text-xs text-pb-text-muted mt-0.5 truncate">
                  {subtitle}
                </div>
              ) : null}
            </div>
          </div>
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- not an interactive control; the handlers only stop propagation so clicking the header buttons doesn't trigger the card's onSelect. */}
          <div
            className="flex items-center gap-1 shrink-0 opacity-80 group-hover:opacity-100 transition-opacity"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {headerExtras}
            {onAskAI ? (
              <HeaderIconButton
                onClick={onAskAI}
                title="Ask PlayerAI about this"
              >
                <Sparkles className="w-3.5 h-3.5" />
              </HeaderIconButton>
            ) : null}
            {onRefresh ? (
              <HeaderIconButton onClick={onRefresh} title="Refresh">
                <RefreshCw className="w-3.5 h-3.5" />
              </HeaderIconButton>
            ) : null}
            {onExpand ? (
              <HeaderIconButton onClick={onExpand} title="Expand">
                <Maximize2 className="w-3.5 h-3.5" />
              </HeaderIconButton>
            ) : null}
            {onMore ? (
              <HeaderIconButton onClick={onMore} title="More">
                <Ellipsis className="w-3.5 h-3.5" />
              </HeaderIconButton>
            ) : null}
          </div>
        </div>
        <div className="px-4 py-4 flex-1 min-h-0 flex flex-col">{children}</div>
        {footer ? (
          <div className="px-4 py-2 border-t border-pb-border bg-pb-card-elevated text-dense-1 text-pb-text-muted flex items-center justify-between gap-2 shrink-0">
            {footer}
          </div>
        ) : null}
      </div>
    </>
  );
}

function HeaderIconButton({
  children,
  onClick,
  title,
}: {
  children: ReactNode;
  onClick: () => void;
  title: string;
}): JSX.Element {
  return (
    // The label repeats the name, so it describes nothing more.
    <Tooltip content={title} describeChild={false}>
      {/* eslint-disable-next-line ds/no-hand-built -- kept: a 26px muted-ink icon box; Button ghost iconOnly is 24px or 28px with secondary ink, and no approved look row lists the change. */}
      <button
        type="button"
        onClick={onClick}
        aria-label={title}
        className="p-1.5 rounded-md text-pb-text-muted hover:text-pb-text hover:pb-overlay-paint transition-colors"
      >
        {children}
      </button>
    </Tooltip>
  );
}
